import React, { useState, FC } from 'react'
import { Stack, Box, useTheme, Theme } from '@mui/material'
import { BasicResult, Prescription } from '../../../../types/models/Presc'
import { DISPLAY_ITEMS } from '../../../../constants/const'
import { localStorageWrapper } from '../../../../helpers/localStorage'
import { QRImage } from '../../../../components/molecules/QRImage'
import { useGenerateCsvButton } from '../../../../features/prescription'

// 共通のBasicResultMenuを再利用するためのインポート
// BasicResultMenuは内部コンポーネントなので、元のファイルから必要な部分を再現します
import {
  BASIC_RESULT_LABEL,
  EDITABLE_ITEM_KEYS,
  EDITABLE_ITEM_WITH_SELECT_BOX_KEYS,
  NON_EDITABLE_ITEM_KEYS,
  REPORT_CHECKBOXES_ITEM_KEYS,
  CHECKBOX_ITEM_KEYS,
  INPUTBOX_MAX_WIDTH,
  CHECKBOX_MAX_WIDTH,
} from '../../../../constants/const'
import {
  EditBasicResultKey,
  InputBoxKey,
  CheckBoxKey,
  DisplayItemKey,
} from '../../../../types/models/Presc'
import { addIsSelectedProp } from '../../../../helpers/addIsSelectedPresc'
import {
  ReportCheckboxes,
  ReportCheckBox,
} from '../../../../components/molecules/Checkbox'
import {
  useKanaEditModal,
  useInsuranceNumberEditModal,
  useBirthdaySelectModal,
} from '../../../../features/users'
import { HighlightInputBox } from '../../../../components/molecules/HighlightInputBox'
import { EditableInputBox } from '../../../../components/molecules/EditableInputBox'
import { PRESCRIPTION_INFO } from '../../../../constants/const'

type EditableItemKey =
  | 'id_11_patient_name_kana'
  | 'id_22_insurance_patient_num'
  | 'id_13_patient_birthday'

type BasicResultWindowProps = {
  editBasicResult: (key: EditBasicResultKey) => void
  reportBasicResult: (key: keyof BasicResult, isReported: boolean) => void
  selectedPresc: Prescription
  isCheckboxDisabled: boolean
  openQrImageModal: () => void
  getHasHighCheckPriority: (user_check_priority: number) => boolean
}

type BasicResultMenuProps = {
  editBasicResult: (key: EditBasicResultKey) => void
  reportBasicResult: (key: keyof BasicResult, isReported: boolean) => void
  basicResult: BasicResult
  prescId: string
  isCheckboxDisabled: boolean
  getHasHighCheckPriority: (user_check_priority: number) => boolean
  displayItems: DisplayItemKey[]
  inTwoColumns: boolean
}

const isEditable = (key: EditableItemKey) =>
  key === 'id_11_patient_name_kana'
    ? localStorageWrapper.getItem('isPatientKanaEditable')
    : key === 'id_22_insurance_patient_num'
    ? localStorageWrapper.getItem('isInsuranceNumEditable')
    : localStorageWrapper.getItem('isBirthdayEditable')

const BasicResultMenu: FC<BasicResultMenuProps> = ({
  editBasicResult,
  reportBasicResult,
  basicResult,
  prescId,
  isCheckboxDisabled,
  getHasHighCheckPriority,
  displayItems,
  inTwoColumns,
}) => {
  const {
    openEditableModal: openKanaEditableModal,
    renderEditableModal: renderKanaEditableModal,
  } = useKanaEditModal(prescId, basicResult, editBasicResult)

  const {
    openEditableModal: openInsuranceNumEditableModal,
    renderEditableModal: renderInsuranceNumEditableModal,
  } = useInsuranceNumberEditModal(prescId, basicResult, editBasicResult)

  const {
    openEditableModal: openBirthdaySelectModal,
    renderEditableModal: renderBirthdaySelectModal,
  } = useBirthdaySelectModal(prescId, basicResult, editBasicResult)

  const editBasicResultFactory =
    (key: keyof BasicResult) => (value: string) => {
      editBasicResult({
        key,
        value,
      })
    }

  const openEditableModal = (key: InputBoxKey) => {
    return key === 'id_11_patient_name_kana'
      ? openKanaEditableModal
      : key === 'id_22_insurance_patient_num'
      ? openInsuranceNumEditableModal
      : openBirthdaySelectModal
  }

  const getCheckBoxList = (key: CheckBoxKey) => {
    return key === 'id_12_patient_sex'
      ? addIsSelectedProp(key, PRESCRIPTION_INFO.SEX_LIST, basicResult)
      : addIsSelectedProp(key, PRESCRIPTION_INFO.INSURANCE_LIST, basicResult)
  }

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: inTwoColumns ? 'repeat(2, 1fr)' : 'repeat(1, 1fr)',
    gap: '12px',
  }

  return (
    <div style={gridStyle}>
      {displayItems.map((item) => (
        <div key={item}>
          {EDITABLE_ITEM_KEYS.includes(item) &&
            (isEditable(item as EditableItemKey) ? (
              <EditableInputBox
                onClick={
                  isEditable(item as EditableItemKey)
                    ? openEditableModal(item as InputBoxKey)
                    : () =>
                        reportBasicResult(item, !basicResult[item].is_reported)
                }
                label={BASIC_RESULT_LABEL[item]}
                isHighlighted={basicResult[item].is_reported}
                value={basicResult[item].selected_option}
                maxWidth={INPUTBOX_MAX_WIDTH.patientInfo}
                hasHighCheckPriority={getHasHighCheckPriority(
                  basicResult[item].user_check_priority
                )}
              />
            ) : (
              <HighlightInputBox
                onClick={
                  isEditable(item as EditableItemKey)
                    ? openEditableModal(item as InputBoxKey)
                    : () =>
                        reportBasicResult(item, !basicResult[item].is_reported)
                }
                label={BASIC_RESULT_LABEL[item]}
                isHighlighted={basicResult[item].is_reported}
                value={basicResult[item].selected_option}
                maxWidth={INPUTBOX_MAX_WIDTH.patientInfo}
                hasHighCheckPriority={getHasHighCheckPriority(
                  basicResult[item].user_check_priority
                )}
              />
            ))}
          {EDITABLE_ITEM_WITH_SELECT_BOX_KEYS.includes(item) &&
            (isEditable(item as EditableItemKey) ? (
              <EditableInputBox
                onClick={
                  isEditable(item as EditableItemKey)
                    ? openEditableModal(item as InputBoxKey)
                    : () =>
                        reportBasicResult(item, !basicResult[item].is_reported)
                }
                label={BASIC_RESULT_LABEL[item]}
                isHighlighted={basicResult[item].is_reported}
                value={basicResult[item].selected_option}
                maxWidth={INPUTBOX_MAX_WIDTH.patientInfo}
                hasHighCheckPriority={getHasHighCheckPriority(
                  basicResult[item].user_check_priority
                )}
              />
            ) : (
              <HighlightInputBox
                onClick={
                  isEditable(item as EditableItemKey)
                    ? openEditableModal(item as InputBoxKey)
                    : () =>
                        reportBasicResult(item, !basicResult[item].is_reported)
                }
                label={BASIC_RESULT_LABEL[item]}
                isHighlighted={basicResult[item].is_reported}
                value={basicResult[item].selected_option}
                maxWidth={INPUTBOX_MAX_WIDTH.patientInfo}
                hasHighCheckPriority={getHasHighCheckPriority(
                  basicResult[item].user_check_priority
                )}
              />
            ))}
          {NON_EDITABLE_ITEM_KEYS.includes(item) && (
            <HighlightInputBox
              onClick={() =>
                reportBasicResult(item, !basicResult[item].is_reported)
              }
              label={BASIC_RESULT_LABEL[item]}
              isHighlighted={basicResult[item].is_reported}
              value={basicResult[item].selected_option}
              maxWidth={INPUTBOX_MAX_WIDTH.patientInfo}
              hasHighCheckPriority={getHasHighCheckPriority(
                basicResult[item].user_check_priority
              )}
            />
          )}
          {REPORT_CHECKBOXES_ITEM_KEYS.includes(item) && (
            <ReportCheckboxes
              onChange={editBasicResultFactory(item)}
              label={BASIC_RESULT_LABEL[item]}
              checkboxList={getCheckBoxList(item as CheckBoxKey)}
              isDisabled={isCheckboxDisabled}
              maxWidth={CHECKBOX_MAX_WIDTH.patientInfo}
              hasHighCheckPriority={getHasHighCheckPriority(
                basicResult[item].user_check_priority
              )}
              isReported={basicResult[item].is_reported}
            />
          )}
          {CHECKBOX_ITEM_KEYS.includes(item) && (
            <ReportCheckBox
              label={BASIC_RESULT_LABEL[item]}
              isChecked={basicResult[item].is_reported}
              onChange={() =>
                reportBasicResult(item, !basicResult[item].is_reported)
              }
            />
          )}
        </div>
      ))}

      {renderKanaEditableModal()}
      {renderInsuranceNumEditableModal()}
      {renderBirthdaySelectModal()}
    </div>
  )
}

const tabBoxProps = (isSelected: boolean, theme: Theme) => {
  const tintColor = theme.palette.secondary.sub
  const baseColor = theme.palette.secondary.main
  return {
    sx: {
      bgcolor: isSelected ? tintColor : baseColor,
    },
    width: '50%',
    color: '#fff',
    borderRadius: '8px 8px 0px 0px',
    textAlign: 'center' as const,
  }
}

// Melphin専用のPatientMatchingBasicResultWindow (変更なし)
/**
 * JAHIS QR直読(was_read_using_jahis_qr=true)の処方箋は backend が basic_result=null を返す。
 * 項目メニューは描けないので何も出さない(タブ・CSVボタン等の周辺UIはそのまま残す)。
 */
const BasicResultMenuIfPresent: FC<
  Omit<BasicResultMenuProps, 'basicResult'> & {
    basicResult: BasicResult | null
  }
> = ({ basicResult, ...rest }) =>
  basicResult ? <BasicResultMenu basicResult={basicResult} {...rest} /> : null

export const MelphinPatientMatchingBasicResultWindow: FC<
  BasicResultWindowProps
> = ({
  editBasicResult,
  reportBasicResult,
  selectedPresc,
  isCheckboxDisabled,
  openQrImageModal,
  getHasHighCheckPriority,
}) => {
  const theme = useTheme()
  const PatientMatchingBasicResultTab = (
    <Stack direction="row">
      <Box {...tabBoxProps(true, theme)}>{'患者一致情報'}</Box>
    </Stack>
  )

  const patientMatchingDisplayItems = (() => {
    const displayItem =
      localStorageWrapper.getItem('patientMatchingDisplayItems') ||
      DISPLAY_ITEMS
    return displayItem
  })()

  const CsvButton = useGenerateCsvButton(selectedPresc?.id)
  return (
    <Box>
      {PatientMatchingBasicResultTab}
      <Stack
        p={1}
        bgcolor="#fff"
        borderRadius="0px 0px 8px 8px"
        direction="row"
        alignItems="flex-start"
      >
        <Stack
          sx={{ boxSizing: 'border-box' }}
          spacing={2}
          p={1}
          width="50%"
          display="inline-block"
        >
          <BasicResultMenuIfPresent
            prescId={selectedPresc.id}
            reportBasicResult={reportBasicResult}
            editBasicResult={editBasicResult}
            basicResult={selectedPresc.basic_result}
            isCheckboxDisabled={isCheckboxDisabled}
            getHasHighCheckPriority={getHasHighCheckPriority}
            displayItems={patientMatchingDisplayItems}
            inTwoColumns={false}
          />
        </Stack>
        <Stack
          sx={{ boxSizing: 'border-box' }}
          p={1}
          width="50%"
          display="inline-block"
        >
          <QRImage
            prescription={selectedPresc}
            isLoadingNewQr={isCheckboxDisabled}
            openModal={openQrImageModal}
          />
          {CsvButton}
        </Stack>
      </Stack>
    </Box>
  )
}

// Melphin専用: [その他報告]タブを削除したバージョン
export const MelphinNonMatchingBasicResultWindow: FC<
  BasicResultWindowProps
> = ({
  editBasicResult,
  reportBasicResult,
  selectedPresc,
  isCheckboxDisabled,
  getHasHighCheckPriority,
}) => {
  const theme = useTheme()
  const [mainBasicResultShown, setMainBasicResultShown] = useState(true)

  const NonMatchingBasicResultTab = (
    <Stack direction="row">
      <Box
        {...tabBoxProps(mainBasicResultShown, theme)}
        onClick={() => {
          setMainBasicResultShown(true)
        }}
      >
        {'基本情報'}
      </Box>
      <Box
        {...tabBoxProps(!mainBasicResultShown, theme)}
        onClick={() => {
          setMainBasicResultShown(false)
        }}
      >
        {'保険情報'}
      </Box>
      {/* [その他報告]タブを削除 */}
    </Stack>
  )

  const nonMatchingMainDisplayItems = (() => {
    const displayItem =
      localStorageWrapper.getItem('nonMatchingMainDisplayItems') ||
      DISPLAY_ITEMS
    return displayItem
  })()

  const nonMatchingSubDisplayItems = (() => {
    const displayItem =
      localStorageWrapper.getItem('nonMatchingSubDisplayItems') || DISPLAY_ITEMS
    return displayItem
  })()

  return (
    <Box>
      {NonMatchingBasicResultTab}
      <Stack
        p={1}
        bgcolor="#fff"
        borderRadius="0px 0px 8px 8px"
        direction="row"
        alignItems="flex-start"
      >
        <Stack
          sx={{ boxSizing: 'border-box' }}
          spacing={2}
          p={1}
          width="100%"
          display="inline-block"
        >
          {mainBasicResultShown ? (
            <BasicResultMenuIfPresent
              prescId={selectedPresc.id}
              reportBasicResult={reportBasicResult}
              editBasicResult={editBasicResult}
              basicResult={selectedPresc.basic_result}
              isCheckboxDisabled={isCheckboxDisabled}
              getHasHighCheckPriority={getHasHighCheckPriority}
              displayItems={nonMatchingMainDisplayItems}
              inTwoColumns={true}
            />
          ) : (
            <BasicResultMenuIfPresent
              prescId={selectedPresc.id}
              reportBasicResult={reportBasicResult}
              editBasicResult={editBasicResult}
              basicResult={selectedPresc.basic_result}
              isCheckboxDisabled={isCheckboxDisabled}
              getHasHighCheckPriority={getHasHighCheckPriority}
              displayItems={nonMatchingSubDisplayItems}
              inTwoColumns={true}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  )
}
